import DefaultButton from '@/components/common/default/DefaultButton';
import { PrimaryButtonLink } from '@/components/common/PrimaryButton';
import IndexBottleOfTheDaySkeleton from '@/components/common/Skeleton/IndexBottleOfTheDaySkeleton';
import Container from '@/components/Container';
import SkuTrendingBadge from '@/components/sku/SkuTrendingBadge';
import SkuDescription from '@/components/skuDescription';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useWishlist from '@/hooks/useWishlist';
import { IndexBottleOfTheDayProps } from '@/types';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { AskMarketValue, BidMarketValue, OrderMarketValue } from '@/types/utilities';
import { SkuMainImage } from '@/utilites/get-sku-main-image';
import { getMarketValue } from '@/utilites/market-value';
import { toDe } from '@/utilites/sku-operations';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { IoIosHeart, IoIosHeartEmpty } from 'react-icons/io';

export default function IndexBottleOfTheDay({ sku, isLoading }: IndexBottleOfTheDayProps) {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const marketValue = getMarketValue(
    (sku?.asks || []) as AskMarketValue[],
    (sku?.bids || []) as BidMarketValue[],
    (sku?.orders || []) as OrderMarketValue[],
    sku?.retailPrice || 0
  );
  const { isInWishlist, toggleFromWishlist } = useWishlist();
  const skuInWishlist = isInWishlist(sku?.slug ?? '');

  if (isLoading) {
    return <IndexBottleOfTheDaySkeleton />;
  }

  if (!sku) {
    return null;
  }

  return (
    <Container
      fluid
      className="tw-border tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-custom-gray tw-bg-[#F5F5F5] nine:tw-mt-[20px]"
    >
      <Container className="tw-flex tw-items-stretch tw-justify-between tw-py-10 max-nine:tw-px-0">
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-6xl tw-items-stretch tw-justify-around max-nine:tw-flex-col max-nine:tw-gap-6">
          <h2 className="tw-relative tw-w-fit tw-text-2xl tw-font-semibold tw-text-text max-nine:tw-text-lg nine:tw-hidden">
            {translateCommon(CommonEnum['bottle_of_the_day'])}
            <hr
              aria-hidden="true"
              className="tw-absolute tw-mt-1 tw-h-[5px] tw-w-[80%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"
            />
          </h2>
          <div className="tw-relative tw-rounded-2xl tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t tw-border-solid tw-border-custom-gray tw-bg-white tw-p-[50px] max-nine:tw-hidden">
            <div className="tw-top-today">{translateCommon(CommonEnum['top_today'])}</div>
            <Link href={`/${sku.slug}`}>
              <SkuMainImage
                src={sku.mainImageUrl}
                alt={sku.name ?? ''}
                width={350}
                height={350}
                className="tw-object-contain"
              />
            </Link>
            <DefaultButton
              onClick={() => toggleFromWishlist(sku)}
              className="tw-absolute tw-right-5 tw-top-3 tw-text-[26px]"
            >
              {skuInWishlist ? (
                <IoIosHeart className="tw-text-red-500" />
              ) : (
                <IoIosHeartEmpty className="tw-text-slate-gray" />
              )}
            </DefaultButton>
          </div>
          <div className="tw-w-2/4 max-nine:tw-w-full">
            <div className="tw-mb-6 max-nine:tw-flex max-nine:tw-items-center max-nine:tw-justify-start max-nine:tw-gap-4">
              <div className="tw-relative tw-h-40 tw-w-40 tw-rounded-2xl tw-bg-white tw-p-3 nine:tw-hidden">
                <Link href={`/${sku.slug}`}>
                  <SkuMainImage
                    src={sku.mainImageUrl}
                    alt={sku.name ?? ''}
                    width={160}
                    height={160}
                    className="tw-h-full tw-w-full tw-object-contain"
                  />
                </Link>
                <DefaultButton
                  onClick={() => toggleFromWishlist(sku)}
                  className="tw-absolute tw-right-5 tw-top-3 tw-text-[26px]"
                >
                  {skuInWishlist ? (
                    <IoIosHeart className="tw-text-red-500" />
                  ) : (
                    <IoIosHeartEmpty className="tw-text-slate-gray" />
                  )}
                </DefaultButton>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-0.5">
                <Link href={`/${sku.slug}`} className="tw-block tw-no-underline">
                  <h2 className="tw-w-fit tw-text-2xl tw-font-semibold tw-text-text max-nine:tw-mb-0 max-nine:tw-text-base">
                    {sku.name}
                    <hr
                      aria-hidden="true"
                      className="tw-my-2 tw-h-[5px] tw-w-[80%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-hidden"
                    />
                  </h2>
                </Link>
                <p className="tw-m-0 tw-p-0 tw-text-left tw-text-base tw-font-semibold tw-text-slate-gray max-nine:tw-text-sm">
                  <SkuDescription
                    skuBottleSizeInMl={sku.bottleSizeInMl}
                    skuReleaseDate={sku.releaseDate}
                    skuVolumeInPercent={sku.volumeInPercent}
                  />
                </p>
                <div className="nine:tw-hidden">
                  <br />
                  <p className="tw-m-0 tw-p-0 tw-text-base tw-font-semibold tw-text-slate-gray max-nine:tw-text-[14px] max-nine:tw-font-medium">
                    {translateCommon(CommonEnum['lowest_ask'])}
                  </p>
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                    <strong className="tw-text-2xl tw-font-semibold tw-text-textBlack max-nine:tw-text-lg">
                      {toDe(sku.lowestAsk || 0)}
                    </strong>
                    <SkuTrendingBadge sku={sku} />
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-block nine:tw-mb-[53px]">
              <p className="tw-tw-flex tw-flex-col tw-gap-2 tw-text-left tw-text-base tw-font-medium tw-leading-[156%] tw-text-[#1A202C] max-nine:tw-text-sm max-nine:tw-text-slate-gray">
                <strong className="tw-mb-tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-text-text max-nine:tw-text-base">
                  About:
                  <br />
                </strong>
                {sku.description ||
                  translateCommon(CommonEnum['bottle_of_the_day_description_fallback'])}
              </p>
            </div>
            <div className="tw-flex tw-w-full tw-flex-col tw-gap-3 nine:tw-hidden">
              <PrimaryButtonLink href={`/${sku.slug}`} className="tw-w-full">
                {translateCommon(CommonEnum['view_the_bottle'])}{' '}
                <ArrowRight className="tw-ml-2 tw-h-5 tw-w-5" />
              </PrimaryButtonLink>
            </div>
            <div className="tw-flex tw-w-full tw-items-end tw-justify-between max-nine:tw-hidden">
              <div className="tw-flex tw-min-w-48 tw-items-center tw-justify-start tw-rounded-xl tw-bg-[#5F6D7E33] tw-p-4 tw-text-left">
                <p className="tw-m-0 tw-p-0 tw-text-base tw-font-semibold tw-text-slate-gray">
                  {translateCommon(CommonEnum['lowest_ask'])}
                  <br />
                  <strong className="tw-text-2xl tw-font-semibold tw-text-textBlack">
                    {sku.lowestAsk && sku.lowestAsk > 0 ? toDe(sku.lowestAsk || 0) : '--'}
                  </strong>
                </p>
              </div>
              <div className="tw-flex tw-w-2/4 tw-flex-col tw-gap-3">
                <PrimaryButtonLink href={`/${sku.slug}`} className="tw-w-full">
                  {translateCommon(CommonEnum['view_the_bottle'])}{' '}
                  <ArrowRight className="tw-ml-2 tw-h-5 tw-w-5" />
                </PrimaryButtonLink>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Container>
  );
}
